import React, { useEffect, useMemo } from 'react';
import { DefaultValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  statusMap,
  reasonsMap,
  reasonOptions,
  reasonsFieldsMap,
  type ReasonOption,
  companyNextStatusMap,
  DocumentRequestStatus,
} from '@area-restrita/shared/document/document-requests';

import { Button } from '@/components/ui/button';
import InlineInput from '@/components/InlineInput';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormField } from '@/components/ui/form';
import Select, { SelectOption } from '@/components/Select';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import DocumentSelector from '@/components/QuerySelectors/DocumentSelector';
import {
  CompanyDocumentRequestDefaultValue,
  companyDocumentRequestSchema,
  type CompanyDocumentRequestSchema,
} from './schema';

interface IProps {
  defaultValues?: DefaultValues<CompanyDocumentRequestDefaultValue>;
  documentRequestId?: number;
  readOnly?: boolean;
  canSave: boolean;
  onSubmit: SubmitHandler<CompanyDocumentRequestSchema>;
}

const CompanyDocumentRequestForm: React.FC<IProps> = ({
  defaultValues,
  documentRequestId,
  readOnly,
  canSave,
  onSubmit,
}) => {
  const editMode = !!defaultValues;

  const inRequestStatus = useMemo(
    () => defaultValues?.status?.value === 'request',
    [defaultValues?.status],
  );

  const statusOptions = useMemo(
    () =>
      defaultValues?.status?.value
        ? companyNextStatusMap[
            defaultValues.status.value as DocumentRequestStatus
          ].map((status) => statusMap[status])
        : [],
    [defaultValues?.status],
  );

  const hasNextStatus = useMemo(
    () => statusOptions.length > 0,
    [statusOptions],
  );

  const form = useForm<CompanyDocumentRequestSchema>({
    defaultValues: defaultValues ?? {
      status: statusMap.request,
      reason: reasonsMap.new_entry,
    },
    resolver: zodResolver(companyDocumentRequestSchema),
  });

  const {
    control,
    watch,
    setValue,
    formState: { isLoading },
  } = form;

  const reason = watch('reason') as ReasonOption;

  useEffect(() => {
    const { unsubscribe } = watch((_, { name }) => {
      if (name === 'reason') {
        setValue('reasonFields', {});
      }
    });

    return unsubscribe;
  }, [watch, setValue]);

  const reasonFields = useMemo(
    () => (reason ? reasonsFieldsMap[reason.value].fields : []),
    [reason],
  );

  return (
    <Card>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, console.log)}>
          <CardContent className="flex flex-col gap-6 pt-6">
            {!inRequestStatus &&
              defaultValues?.hasUploadedFile &&
              documentRequestId && (
                <InlineInput
                  title="Arquivo"
                  as={() => (
                    <Link
                      to="/docs/document-request/$documentRequestId"
                      params={{ documentRequestId }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full"
                    >
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Visualizar documento atual
                      </Button>
                    </Link>
                  )}
                />
              )}

            {editMode && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <InlineInput
                      title="Status"
                      as={(props: {
                        value: SelectOption;
                        onChange: (value: SelectOption) => void;
                      }) => (
                        <Select
                          options={statusOptions}
                          {...props}
                          multiple={false}
                        />
                      )}
                      disabled={!hasNextStatus}
                      {...field}
                    />
                  )}
                />

                {!inRequestStatus && defaultValues?.hasUploadedFile && (
                  <InlineInput
                    title="Justificativa"
                    as={Textarea}
                    disabled
                    value={defaultValues.justification ?? ''}
                  />
                )}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <FormField
                control={control}
                name="document"
                render={({ field }) => (
                  <InlineInput
                    title="Documento"
                    as={(props: {
                      value: SelectOption;
                      onChange: (value: SelectOption) => void;
                    }) => (
                      <DocumentSelector
                        {...props}
                        multiple={false}
                        allowsChangeRequest
                        placeholder="Selecione o documento a ser alterado"
                      />
                    )}
                    disabled={readOnly || isLoading}
                    {...field}
                  />
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <FormField
                control={control}
                name="reason"
                render={({ field }) => (
                  <InlineInput
                    title="Motivo"
                    as={(props: {
                      value: SelectOption;
                      onChange: (value: SelectOption) => void;
                    }) => (
                      <Select
                        options={reasonOptions}
                        {...props}
                        multiple={false}
                      />
                    )}
                    disabled={readOnly || isLoading}
                    {...field}
                  />
                )}
              />
              {reasonFields.map(({ name, label }) => (
                <FormField
                  key={`${reason?.value}-${name}`}
                  control={control}
                  name={`reasonFields.${name}`}
                  render={({ field }) => (
                    <InlineInput
                      id={`${reason?.value}-${name}`}
                      title={label}
                      disabled={readOnly || isLoading}
                      {...field}
                      value={field.value || ''}
                    />
                  )}
                />
              ))}
            </div>

            <div className="flex flex-col gap-2">
              <FormField
                control={control}
                name="dateForApproval"
                render={({ field }) => (
                  <InlineInput
                    title="Data para aprovação"
                    type="date"
                    disabled={readOnly || isLoading}
                    {...field}
                  />
                )}
              />
              <FormField
                control={control}
                name="observation"
                render={({ field }) => (
                  <InlineInput
                    title="Observações"
                    as={Textarea}
                    disabled={readOnly || isLoading}
                    {...field}
                  />
                )}
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button type="submit" disabled={!canSave || isLoading}>
              Salvar
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default CompanyDocumentRequestForm;
